import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import { aplicarIbansAgencia, importesDesdeImporteUnico } from "../../utils/empresaExpediente";
import { formatEuroConSimbolo, parseEuroAmount } from "../../utils/moneyParse";

const CONCEPTOS = ["Señal", "Ampliación señal", "Entrega propietario"];
const MEDIOS = ["Transferencia", "Cheque", "Bizum", "Efectivo"];
const ENTREGA_TIPOS = [
  { v: "comprador", l: "Comprador" },
  { v: "agencia", l: "Agencia" },
  { v: "propietario", l: "Propietario" },
];

const VACIO = (empresa) => {
  const base = {
    fecha: new Date().toISOString().slice(0, 10),
    concepto: "Señal",
    iban_salida: "",
    iban_entrada: "",
    entrega_tipo: "comprador",
    recibe_tipo: "agencia",
    medio: "Transferencia",
    importe: "",
    tiene_scan: false,
  };
  return { ...base, ...aplicarIbansAgencia({ ...base, empresa }) };
};

export default function ModalInsertarSenal({ isOpen, onClose, onConfirm, empresa = "Vitalcasa", puedeModificar = true }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const tx = isDark ? "#ffffff" : c.textPrimary;

  const [form, setForm] = useState(() => VACIO(empresa));
  const [importeEdit, setImporteEdit] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(VACIO(empresa));
      setImporteEdit(false);
    }
  }, [isOpen, empresa]);

  if (!isOpen) return null;

  const inp = {
    padding: "6px 8px", borderRadius: 5, fontSize: 12, width: "100%",
    background: isDark ? "#1a2030" : c.inputBg,
    border: `1px solid ${isDark ? "#5a6a80" : c.inputBorder}`,
    color: tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  };
  const lbl = { fontSize: 10, fontWeight: 700, color: tx, marginBottom: 3, display: "block" };
  const btn = {
    padding: "6px 14px", borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: "pointer",
    border: "none", fontFamily: "inherit",
  };

  const patchForm = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      const ibans = aplicarIbansAgencia({
        entrega_tipo: next.entrega_tipo,
        recibe_tipo: next.recibe_tipo,
        iban_salida: next.iban_salida,
        iban_entrada: next.iban_entrada,
        empresa,
      });
      return { ...next, ...ibans };
    });
  };

  const setF = (k, v) => {
    if (k === "entrega_tipo" || k === "recibe_tipo") {
      patchForm({ [k]: v });
      return;
    }
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const confirmar = () => {
    const importeNum = parseEuroAmount(form.importe);
    const importeStr = Number.isFinite(importeNum) ? String(importeNum) : String(form.importe || "").trim();
    const importes = importesDesdeImporteUnico(importeStr, form.recibe_tipo);
    onConfirm({
      fecha: form.fecha,
      concepto: form.concepto,
      iban_salida: form.iban_salida,
      iban_entrada: form.iban_entrada,
      entrega_tipo: form.entrega_tipo,
      recibe_tipo: form.recibe_tipo,
      medio: form.medio,
      tiene_scan: form.tiene_scan,
      ...importes,
    });
    setForm(VACIO(empresa));
    onClose();
  };

  const cancelar = () => {
    setForm(VACIO(empresa));
    onClose();
  };

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 12000,
        background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) cancelar(); }}
    >
      <div style={{
        background: isDark ? "#1e2330" : "#fff",
        border: `1px solid ${isDark ? "#4a5568" : c.border}`,
        borderRadius: 8, padding: "14px 16px", width: 400, maxWidth: "94vw",
        boxShadow: "0 16px 40px rgba(0,0,0,0.25)", color: tx,
      }}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Insertar señal</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={lbl}>Fecha</label>
            <input type="date" style={inp} value={form.fecha} onChange={(e) => setF("fecha", e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Concepto</label>
            <select style={inp} value={form.concepto} onChange={(e) => setF("concepto", e.target.value)}>
              {CONCEPTOS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Quien entrega</label>
            <select style={inp} value={form.entrega_tipo} onChange={(e) => setF("entrega_tipo", e.target.value)}>
              {ENTREGA_TIPOS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Quien recibe</label>
            <select style={inp} value={form.recibe_tipo} onChange={(e) => setF("recibe_tipo", e.target.value)}>
              {ENTREGA_TIPOS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>IBAN salida</label>
            <input style={inp} value={form.iban_salida} onChange={(e) => setF("iban_salida", e.target.value)} />
          </div>
          <div>
            <label style={lbl}>IBAN entrada</label>
            <input style={inp} value={form.iban_entrada} onChange={(e) => setF("iban_entrada", e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Importe €</label>
            <input
              style={{ ...inp, textAlign: "right" }}
              value={importeEdit ? form.importe : formatEuroConSimbolo(form.importe)}
              onFocus={() => {
                setImporteEdit(true);
                const n = parseEuroAmount(form.importe);
                setForm((prev) => ({ ...prev, importe: Number.isFinite(n) ? String(n) : prev.importe }));
              }}
              onBlur={() => {
                setImporteEdit(false);
                const n = parseEuroAmount(form.importe);
                if (Number.isFinite(n)) setForm((prev) => ({ ...prev, importe: String(n) }));
              }}
              onChange={(e) => setF("importe", e.target.value)}
              placeholder="0,00 €"
            />
          </div>
          <div>
            <label style={lbl}>Medio</label>
            <select style={inp} value={form.medio} onChange={(e) => setF("medio", e.target.value)}>
              {MEDIOS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <button type="button" style={{ ...btn, background: isDark ? "#374151" : "#e5e7eb", color: tx }} onClick={cancelar}>
            Cancelar
          </button>
          <button
            type="button"
            style={{ ...btn, background: c.accentBlue, color: "#fff", opacity: puedeModificar ? 1 : 0.5, cursor: puedeModificar ? "pointer" : "not-allowed" }}
            onClick={confirmar}
            disabled={!puedeModificar}
            title={puedeModificar ? undefined : "No tienes permiso para insertar señales"}
          >
            Insertar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
